'use client'

import * as React from 'react'
import { useRouter } from 'next/navigation'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Icons } from '@/components/ui/icons'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

// Notification types
type NotificationType = 'info' | 'success' | 'warning' | 'error'

export interface Notification {
  id: string
  type: NotificationType
  title: string
  message: string
  timestamp: Date
  read: boolean
  /** In-app link opened on click, e.g. a driver record from the Command Briefing feed. */
  href?: string
}

const typeConfig: Record<
  NotificationType,
  { icon: React.ElementType; color: string; bg: string }
> = {
  // Brand and status tokens (plan 03 CHG-16), not raw Tailwind palette colours.
  info: { icon: Icons.info, color: 'text-brand-purple', bg: 'bg-brand-cornflower/15' },
  success: {
    icon: Icons.checkCircle,
    color: 'text-success-strong',
    bg: 'bg-success-strong/10',
  },
  warning: {
    icon: Icons.alertTriangle,
    color: 'text-warning-strong',
    bg: 'bg-warning-wash',
  },
  error: {
    icon: Icons.alertCircle,
    color: 'text-destructive',
    bg: 'bg-destructive/10',
  },
}

function formatRelativeTime(date: Date): string {
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

interface NotificationItemProps {
  notification: Notification
  onMarkAsRead: (id: string) => void
}

function NotificationItem({
  notification,
  onMarkAsRead,
}: NotificationItemProps) {
  const config = typeConfig[notification.type]
  const Icon = config.icon

  return (
    <button
      type='button'
      className={cn(
        'flex w-full gap-3 rounded-xl p-3 text-left',
        'transition-colors duration-200 ease-out',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-cornflower',
        notification.read ? 'opacity-70 hover:opacity-100' : 'hover:bg-brand-cornflower/5'
      )}
      onClick={() => onMarkAsRead(notification.id)}
    >
      <div className={cn('flex-shrink-0 rounded-lg p-2', config.bg)} aria-hidden='true'>
        <Icon className={cn('h-4 w-4', config.color)} strokeWidth={1.5} />
      </div>
      <div className='min-w-0 flex-1'>
        <div className='flex items-start justify-between gap-2'>
          <p className='truncate text-sm font-medium text-foreground'>
            {notification.title}
          </p>
          {!notification.read && (
            <span className='mt-1.5 h-2 w-2 shrink-0 rounded-full bg-brand-cornflower motion-safe:animate-pulse'>
              <span className='sr-only'>Unread</span>
            </span>
          )}
        </div>
        <p className='mt-0.5 line-clamp-2 text-xs text-muted-foreground'>
          {notification.message}
        </p>
        <p className='mt-1 text-xs text-muted-foreground'>
          {formatRelativeTime(notification.timestamp)}
        </p>
      </div>
    </button>
  )
}

/**
 * Header bell (PA header, plan 03 CHG-18). The template shipped three canned
 * notifications; they are gone (D19). The Header passes the Command Briefing
 * feed (plan 03 CHG-13): drivers that need a decision, drivers waiting on an
 * upload, and new leads. A click opens the row's link and marks it read; read
 * ids are kept across polls, so a refresh does not bring a read row back.
 */
const NO_NOTIFICATIONS: Notification[] = []

export function NotificationCenter({
  notifications: incoming = NO_NOTIFICATIONS,
}: {
  notifications?: Notification[]
}) {
  const router = useRouter()
  const [open, setOpen] = React.useState(false)
  const [readIds, setReadIds] = React.useState<Set<string>>(() => new Set())
  const notifications = React.useMemo(
    () => incoming.map((n) => ({ ...n, read: n.read || readIds.has(n.id) })),
    [incoming, readIds]
  )

  const unreadCount = notifications.filter((n) => !n.read).length

  const markAsRead = (id: string) => {
    setReadIds((prev) => new Set(prev).add(id))
    const href = notifications.find((n) => n.id === id)?.href
    if (href) {
      setOpen(false)
      router.push(href)
    }
  }

  const markAllAsRead = () => {
    setReadIds(new Set(notifications.map((n) => n.id)))
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant='ghost'
          size='icon'
          className='relative rounded-full text-muted-foreground hover:text-foreground'
          aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
        >
          <Icons.bell className='h-5 w-5' strokeWidth={1.5} />
          {unreadCount > 0 && (
            <span
              className={cn(
                'absolute -right-0.5 -top-0.5',
                'flex h-4 min-w-4 items-center justify-center',
                'rounded-full bg-destructive px-1',
                'text-xs font-semibold text-white',
                'animate-badge-bounce'
              )}
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align='end' className='w-80 p-0'>
        {/* Header */}
        <div className='flex items-center justify-between border-b border-border/50 px-4 py-3'>
          <h3 className='font-display font-semibold text-foreground'>
            Notifications
          </h3>
          {unreadCount > 0 && (
            <Button
              variant='link'
              size='sm'
              onClick={markAllAsRead}
              className='h-auto p-0 text-xs text-brand-cornflower'
            >
              Mark all as read
            </Button>
          )}
        </div>

        {/* Notification list */}
        <div className='max-h-[300px] overflow-y-auto p-2'>
          {notifications.length === 0 ? (
            <div className='py-8 text-center'>
              <div className='mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted/50'>
                <Icons.checkCircle className='h-6 w-6 text-muted-foreground' />
              </div>
              <p className='text-sm font-medium text-foreground'>
                All caught up!
              </p>
              <p className='mt-1 text-xs text-muted-foreground'>
                No new notifications
              </p>
            </div>
          ) : (
            <div className='space-y-1'>
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onMarkAsRead={markAsRead}
                />
              ))}
            </div>
          )}
        </div>

      </PopoverContent>
    </Popover>
  )
}

